import * as React from 'react';
import { Link } from 'gatsby';
import { library, icon } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLinkedinIn,
  faFacebookF,
  faInstagram,
} from '@fortawesome/free-brands-svg-icons';
import { faLocationDot } from '@fortawesome/free-solid-svg-icons';
import clsx from 'clsx';
import { useLocation } from '@reach/router';
import { getCookie, deleteCookie } from '../lib/utils';
import { extractLanguage } from '../helpers/common';

library.add(faLinkedinIn, faFacebookF, faInstagram, faLocationDot);

function Footer({
  className,
  id: pageFooter = 'page-footer',
  siteMetadata,
  footerEntries,
}) {
  const getStoryblokMaxWidthUrl = React.useCallback((filename) => {
    if (!filename || typeof filename !== 'string') return filename;

    if (!filename.includes('storyblok.com')) {
      return filename;
    }

    if (filename.includes('/m/')) {
      return filename.replace(/\/m\/\d+x\d+\//, '/m/114x0/');
    }

    return `${filename.replace(/\/$/, '')}/m/114x0`;
  }, []);

  const location = useLocation();
  const currentLanguage = extractLanguage(location.pathname);
  const storyBlokFooterData = footerEntries.filter(({ node }) => node.lang === currentLanguage);
  const dataContent = JSON.parse(storyBlokFooterData[0].node.content);
  const newsLetterData = dataContent?.column_1[0];
  const privacyLinks = dataContent?.column_2[1].links;
  const imagesAndLocation = dataContent?.column_3;
  const allBlogs = dataContent.column_2[2];
  let allBlogsUrl = allBlogs.link.url;
  if (!allBlogsUrl) {
    allBlogsUrl = allBlogs.link.cached_url.startsWith('/') ? allBlogs.link.cached_url : `/${allBlogs.link.cached_url}`;
  }

  let legals = siteMetadata.footer.menuLinks.filter(
    (item) => item.title === 'Legal',
  );
  let socials = siteMetadata.footer.menuLinks.filter(
    (item) => item.title === 'Social',
  );
  let locations = siteMetadata.footer.menuLinks.filter(
    (item) => item.title === 'Locations',
  );

  legals = legals.length ? legals[0] : null;
  socials = socials.length ? socials[0] : null;
  locations = locations.length ? locations[0] : null;

  const [isKlaviyoAccepted, setIsKlaviyoAccepted] = React.useState(false);

  React.useEffect(() => {
    const checkKlaviyoCookie = () => {
      const klaviyoCookie = getCookie('gatsby-gdpr-klaviyo');
      setIsKlaviyoAccepted(klaviyoCookie === 'true');
    };

    checkKlaviyoCookie();
    document.documentElement.addEventListener('cookies:updated', checkKlaviyoCookie);

    return () => {
      document.documentElement.removeEventListener('cookies:updated', checkKlaviyoCookie);
    };
  }, []);

  function openCookieSettings() {
    Object.keys(window.gatsbyPluginGDPRCookiesOptions).forEach((key) => {
      if (key !== 'environments' && key !== 'plugins' && key) {
        localStorage.removeItem(
          window.gatsbyPluginGDPRCookiesOptions[key].cookieName,
        );
        if (
          getCookie(window.gatsbyPluginGDPRCookiesOptions[key].cookieName)
          !== ''
        ) {
          localStorage.setItem(
            window.gatsbyPluginGDPRCookiesOptions[key].cookieName,
            getCookie(window.gatsbyPluginGDPRCookiesOptions[key].cookieName),
          );
        }
        deleteCookie(window.gatsbyPluginGDPRCookiesOptions[key].cookieName);
      }
    });
    localStorage.removeItem('cookieConsent');
    deleteCookie('cookieConsent');
    window.location.reload();
  }

  return (
    <>
      {/* <Script
        src="https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=UjZT3H"
      /> */}
      <footer
        id={pageFooter}
        className={`bg-background-light dark:bg-background-footer-light md:dark:bg-background-footer-dark border-t transition-all ${className}  id="page-footer"`}
      >
        <div className="px-4 lg:px-8 text-center lg:text-left">
          <div className="mx-auto max-w-screen-2xl py-10 text-center md:text-left">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-8">
              <div className="flex justify-start md:justify-start flex-col gap-4 md:max-w-xs text-left">
                {newsLetterData.title}
                {isKlaviyoAccepted ? (
                  <div
                    className={`klaviyo-form-${newsLetterData.form_id} min-h-[50px]`}
                    data-klaviyo-accepted="true"
                  />
                ) : (
                  <div className="min-h-[50px]">
                    <button
                      type="button"
                      className="underline cursor-pointer"
                      onClick={openCookieSettings}
                    >
                      Bitte stimmen Sie erst den Cookies zu
                    </button>
                    <span>, um das Newsletter-Formular zu laden.</span>
                  </div>
                )}
              </div>
              <div className="row-start-4 md:row-start-auto">
                {socials && (
                  <ul className="flex flex-row justify-center md:justify-end lg:justify-center gap-4 items-end h-full">
                    {socials.links.map((social) => (
                      <li key={social.name}>
                        <a
                          href={social.link}
                          title={social.name}
                          target="_blank"
                          rel="noreferrer"
                          className={clsx(
                            social.icon === 'facebook-f'
                              ? ' pt-[0.35rem] !text-xl'
                              : '',
                            'rounded-full overflow-hidden bg-background-dark dark:bg-white text-white dark:text-background-dark w-6 h-6 flex justify-center items-center text-sm',
                          )}
                        >
                          <FontAwesomeIcon
                            icon={icon({
                              prefix: 'fab',
                              iconName: social.icon,
                            })}
                          />
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="flex flex-col gap-4 justify-center md:justify-start lg:justify-end lg:items-end md:col-span-2 lg:col-span-1 h-full">
                <div className="flex gap-4 justify-center items-center flex-wrap sm:flex-nowrap">
                  {imagesAndLocation[0].images.map(({ filename, alt, source }) => {
                    const optimizedFilename = getStoryblokMaxWidthUrl(filename);

                    if (source) {
                      return (
                        <Link
                          key={filename}
                          to={source}
                          className="lg:flex-grow flex-shrink-0 basis-auto w-1/3 flex justify-center sm:justify-start"
                          aria-label={alt || (currentLanguage === 'en' ? 'Visit the award-winning Shape Republic website' : 'Zur preisgekrönten Shape Republic Website')}
                        >
                          <img
                            src={optimizedFilename}
                            width="80"
                            height="80"
                            alt={alt || 'Shopify Premier Partner'}
                            loading="lazy"
                            className="h-20"
                          />
                        </Link>
                      );
                    }
                    return (
                      <img
                        key={filename}
                        src={optimizedFilename}
                        width="178"
                        height="32"
                        alt={alt || (currentLanguage === 'en ' ? 'Award-winning website: Shape Republic by Latori' : 'Ausgezeichnete Website: Shape Republic von Latori')}
                        loading="lazy"
                        className="hidden max-h-[76px] dark:block w-1/3"
                      />
                    );
                  })}


                </div>
              </div>
              {legals && (
                <ul className="flex justify-center md:justify-end lg:justify-center items-start lg:col-start-2 flex-wrap gap-y-4">
                  {privacyLinks?.map((item, index) => (
                    <li
                      className={`pr-2 border-background-dark dark:border-white leading-none${
                        privacyLinks.length - 1 > index
                          ? ' border-r-2 pl-2'
                          : ''
                      }${
                        privacyLinks.length - 1 === index ? ' pl-2 pr-0' : ''
                      }`}
                      key={item._uid}
                    >
                      {item.title.toLowerCase().includes('cookie') ? (
                        <button
                          type="button"
                          className="cursor-pointer"
                          onClick={openCookieSettings}
                        >
                          {item.title}
                        </button>
                      ) : (
                        <Link to={`/${item.link.story?.full_slug
                          || item.link.cached_url}`}
                        >
                          {item.title}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {locations && (
                <ul className="flex justify-center lg:justify-end lg:col-start-3 items-start flex-wrap gap-y-4">
                  {locations.links.map((location, index) => (
                    <li
                      className={`flex gap-2 ${index !== (locations.links.length - 1) ? 'pr-4' : ''} border-background-dark dark:border-white leading-none${
                        locations.links.length - 1 > index ? ' border-r-2' : ''
                      }${
                        locations.links.length - 1 === index ? ' pl-4 pr-0' : ''
                      }`}
                      key={location.name}
                    >
                      <Link to="/about-us#locations">
                        <FontAwesomeIcon icon={icon(faLocationDot)} />
                        {' '}
                        {location.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mx-auto w-full flex justify-center lg:col-span-3 items-center">
                {allBlogs && (
                  <Link to={allBlogsUrl} className="inline-flex text-xs">
                    {allBlogs.title}
                  </Link>
                )}

              </div>
              <div className="mx-auto md:col-span-2 lg:col-span-3 text-xs text-center opacity-50">
                {dataContent?.footer_text}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

export default Footer;
